import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { VidstackPlayerComponent } from '@/app/components/vidstack-player/vidstack-player';
import { ProcessingLoaderComponent } from '@/app/components/processing-loader/processing-loader';
import {
  WorkspaceAssetModel,
  WorkspaceAssetService,
  WorkspaceAssetType,
} from '@core/services/workspace-asset.service';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { catchError, finalize, forkJoin, map, of, switchMap, take } from 'rxjs';
import { EditorMedia } from './media.types';
import { ToastrService } from 'ngx-toastr';
import {
  AccountPlanLimitService,
  isGeneratedImagesLimitError,
  isMaxAiCreditsMonthLimitError,
  isMaxWorkspaceAssetsLimitError,
} from '@core/services/account-plan-limit.service';
import { normalizeUntrustedText } from '@core/utils/untrusted-text';
import { AiCreditsPurchaseService } from '@core/services/ai-credits-purchase.service';

export type MediaPickerTab = 'ai' | 'library' | 'upload';
type AiImageStyle =
  | 'realistic-illustration'
  | 'realistic'
  | 'educational-illustration'
  | 'vector-icon'
  | 'three-d';
type LibraryMediaFilter =
  | 'all'
  | WorkspaceAssetType.Image
  | WorkspaceAssetType.Audio
  | WorkspaceAssetType.Video;
type MediaPreviewType = 'image' | 'audio' | 'video';

const DEFAULT_AI_IMAGE_STYLE: AiImageStyle = 'realistic-illustration';
const MAX_IMAGE_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_FILE_NAME_LENGTH = 260;
const IMAGE_MIME_TYPES_BY_EXTENSION: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
};

@Component({
  selector: 'app-media-picker-modal',
  imports: [
    ReactiveFormsModule,
    NgbDropdownModule,
    VidstackPlayerComponent,
    ProcessingLoaderComponent,
  ],
  templateUrl: './media-picker-modal.html',
  styleUrl: './media-picker-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.aria-busy]': 'isProcessing() ? "true" : null',
  },
})
export class MediaPickerModalComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly assetService = inject(WorkspaceAssetService);
  private readonly toastr = inject(ToastrService);
  private readonly aiCreditsPurchaseService = inject(AiCreditsPurchaseService);
  private readonly accountPlanLimitService = inject(AccountPlanLimitService);
  private readonly deckTranslations =
    inject(UiTranslationService).namespace('deck');

  initialTab: MediaPickerTab = 'ai';
  generatedImagesBlocked = false;
  imageOnly = false;
  readonly t = this.deckTranslations.t;
  readonly workspaceAssetType = WorkspaceAssetType;
  readonly aiImageStyleOptions: ReadonlyArray<{
    value: AiImageStyle;
    labelKey: string;
  }> = [
    {
      value: 'realistic-illustration',
      labelKey: 'textEditor.mediaStyleRealisticIllustration',
    },
    { value: 'realistic', labelKey: 'textEditor.mediaStyleRealistic' },
    {
      value: 'educational-illustration',
      labelKey: 'textEditor.mediaStyleEducationalIllustration',
    },
    { value: 'vector-icon', labelKey: 'textEditor.mediaStyleVectorIcon' },
    { value: 'three-d', labelKey: 'textEditor.mediaStyleThreeD' },
  ];
  readonly activeTab = signal<MediaPickerTab>('ai');
  readonly isLoadingLibrary = signal(false);
  readonly isGeneratingAiImage = signal(false);
  readonly isUploading = signal(false);
  readonly isProcessing = computed(
    () => this.isGeneratingAiImage() || this.isUploading(),
  );
  readonly libraryMediaAssets = signal<EditorMedia[]>([]);
  readonly selectedLibraryAssetId = signal<number | null>(null);
  readonly libraryAssetFilter = signal<LibraryMediaFilter>('all');
  readonly infoMessage = signal('');
  readonly errorMessage = signal('');
  private selectedObjectUrlToKeep = '';

  readonly aiForm = this.fb.nonNullable.group({
    style: this.fb.nonNullable.control<AiImageStyle>(DEFAULT_AI_IMAGE_STYLE),
    prompt: this.fb.nonNullable.control('', [
      Validators.required,
      Validators.maxLength(200),
    ]),
  });

  readonly filteredLibraryMediaAssets = computed(() => {
    const activeFilter = this.libraryAssetFilter();
    const mediaAssets = this.libraryMediaAssets();
    if (activeFilter === 'all') {
      return mediaAssets;
    }

    return mediaAssets.filter((media) => media.assetType === activeFilter);
  });

  readonly canConfirm = computed(() => {
    if (this.activeTab() === 'library') {
      return this.selectedLibraryAssetId() != null;
    }

    return false;
  });

  canGenerateImage(): boolean {
    return (
      this.activeTab() === 'ai' &&
      this.aiForm.valid &&
      !this.generatedImagesBlocked &&
      !this.isProcessing()
    );
  }

  ngOnInit(): void {
    this.setActiveTab(this.initialTab);
    this.loadLibraryMediaAssets();
  }

  ngOnDestroy(): void {
    this.revokeLibraryObjectUrls();
  }

  tr(key: string, params?: Record<string, string | number>): string {
    return this.deckTranslations.tr(key, params);
  }

  setActiveTab(tab: MediaPickerTab): void {
    if (this.isProcessing()) {
      return;
    }

    this.activeTab.set(tab);
    this.infoMessage.set('');
    this.errorMessage.set('');
  }

  uploadImage(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (input) input.value = '';
    if (!file || this.isProcessing()) return;
    if (!this.isAcceptedImageUpload(file)) {
      this.errorMessage.set(this.tr('textEditor.mediaInvalidType'));
      return;
    }
    this.errorMessage.set('');
    this.isUploading.set(true);
    this.assetService
      .upload({ file, mimeType: file.type })
      .pipe(
        switchMap((asset) =>
          this.assetService
            .resolveBrowserUrl(asset)
            .pipe(map((resolved) => ({ asset, resolved }))),
        ),
        finalize(() => this.isUploading.set(false)),
      )
      .subscribe({
        next: ({ asset, resolved }) => {
          const media = this.toEditorMedia({
            ...asset,
            storagePath: resolved.fileUrl || asset.storagePath,
          });
          if (!media) {
            this.errorMessage.set(this.tr('textEditor.mediaLibraryLoadError'));
            return;
          }
          this.activeModal.close({
            ...media,
            source: 'upload',
            url: resolved.url || media.url,
            fileUrl: resolved.fileUrl || media.fileUrl,
          });
        },
        error: (error: Error) =>
          this.errorMessage.set(
            error?.message ?? this.tr('textEditor.mediaLibraryLoadError'),
          ),
      });
  }

  setLibraryAssetFilter(filter: LibraryMediaFilter): void {
    if (this.isProcessing()) {
      return;
    }

    this.libraryAssetFilter.set(filter);
    const selectedLibraryAssetId = this.selectedLibraryAssetId();
    if (selectedLibraryAssetId == null) {
      return;
    }

    const selectedStillVisible = this.filteredLibraryMediaAssets().some(
      (media) => media.workspaceAssetId === selectedLibraryAssetId,
    );
    if (!selectedStillVisible) {
      this.selectedLibraryAssetId.set(null);
    }
  }

  libraryAssetFilterLabel(): string {
    const activeFilter = this.libraryAssetFilter();
    if (activeFilter === WorkspaceAssetType.Image) {
      return this.tr('textEditor.mediaAssetTypeImage');
    }

    if (activeFilter === WorkspaceAssetType.Audio) {
      return this.tr('textEditor.mediaAssetTypeAudio');
    }

    if (activeFilter === WorkspaceAssetType.Video) {
      return this.tr('textEditor.mediaAssetTypeVideo');
    }

    return this.tr('textEditor.mediaAssetTypeAll');
  }

  selectLibraryMedia(assetId: number): void {
    if (this.isProcessing()) {
      return;
    }

    this.selectedLibraryAssetId.set(assetId);
  }

  isVideoMedia(media: EditorMedia): boolean {
    return this.resolveMediaPreviewType(media) === 'video';
  }

  isAudioMedia(media: EditorMedia): boolean {
    return this.resolveMediaPreviewType(media) === 'audio';
  }

  aiPromptPlaceholder(): string {
    return this.tr('textEditor.mediaPromptPlaceholderImage');
  }

  confirm(): void {
    if (this.isProcessing()) {
      return;
    }

    if (this.activeTab() === 'library') {
      const selectedId = this.selectedLibraryAssetId();
      const selectedMedia = this.libraryMediaAssets().find(
        (media) => media.workspaceAssetId === selectedId,
      );
      if (selectedMedia) {
        if (selectedMedia.url.startsWith('blob:')) {
          this.selectedObjectUrlToKeep = selectedMedia.url;
        }
        this.activeModal.close(selectedMedia);
      }
      return;
    }
  }

  generateAiImage(): void {
    if (this.isProcessing()) {
      return;
    }

    if (this.generatedImagesBlocked) {
      this.showGeneratedImagesLimit();
      return;
    }

    if (this.aiForm.invalid) {
      this.aiForm.markAllAsTouched();
      this.errorMessage.set(this.tr('textEditor.mediaPromptRequired'));
      return;
    }

    const prompt = normalizeUntrustedText(this.aiForm.controls.prompt.value);
    this.aiForm.controls.prompt.setValue(prompt, { emitEvent: false });
    const style = this.aiForm.controls.style.value;
    this.accountPlanLimitService
      .canUseAiCreditsFresh()
      .pipe(take(1))
      .subscribe((canUseAiCredits) => {
        if (!canUseAiCredits) {
          this.aiCreditsPurchaseService.showLimitReached(
            this.deckTranslations.tr('common.aiCreditsLimitReached'),
          );
          return;
        }
        this.generateAiImageRequest(prompt, style);
      });
  }

  private generateAiImageRequest(prompt: string, style: AiImageStyle): void {
    this.infoMessage.set('');
    this.errorMessage.set('');
    this.isGeneratingAiImage.set(true);
    this.assetService
      .generateImage({
        prompt,
        title: prompt,
        style,
      })
      .pipe(
        switchMap((asset) =>
          this.assetService.resolveBrowserUrl(asset).pipe(
            map((resolved) => ({
              asset,
              resolved,
            })),
          ),
        ),
        finalize(() => this.isGeneratingAiImage.set(false)),
      )
      .subscribe({
        next: ({ asset, resolved }) => {
          const media = this.toEditorMedia({
            ...asset,
            storagePath: resolved.fileUrl || asset.storagePath,
          });
          if (!media) {
            this.errorMessage.set(this.tr('textEditor.mediaGenerateError'));
            return;
          }

          const generatedMedia: EditorMedia = {
            ...media,
            source: 'ai',
            url: resolved.url || media.url,
            fileUrl: resolved.fileUrl || media.fileUrl,
          };

          if (generatedMedia.url.startsWith('blob:')) {
            this.selectedObjectUrlToKeep = generatedMedia.url;
          }

          this.activeModal.close(generatedMedia);
        },
        error: (error: Error) => {
          if (isMaxAiCreditsMonthLimitError(error)) {
            this.accountPlanLimitService.refresh().subscribe();
            this.aiCreditsPurchaseService.showLimitReached(
              this.deckTranslations.tr('common.aiCreditsLimitReached'),
            );
            return;
          }
          if (isGeneratedImagesLimitError(error)) {
            this.accountPlanLimitService.refresh().subscribe();
            return;
          }
          if (isMaxWorkspaceAssetsLimitError(error)) {
            this.accountPlanLimitService.refresh().subscribe();
            return;
          }

          this.errorMessage.set(
            error?.message ?? this.tr('textEditor.mediaGenerateError'),
          );
        },
      });
  }

  cancel(): void {
    if (this.isProcessing()) {
      return;
    }

    this.activeModal.dismiss();
  }

  private loadLibraryMediaAssets(): void {
    this.isLoadingLibrary.set(true);
    this.assetService
      .list(this.imageOnly ? WorkspaceAssetType.Image : undefined)
      .pipe(
        switchMap((assets) => {
          const mediaAssets = assets
            .filter((asset) =>
              this.isSupportedLibraryAssetType(asset.assetType),
            )
            .map((asset) => ({
              asset,
              media: this.toEditorMedia(asset),
            }))
            .filter(
              (
                assetEntry,
              ): assetEntry is {
                asset: WorkspaceAssetModel;
                media: EditorMedia;
              } => assetEntry.media != null,
            );

          if (mediaAssets.length === 0) {
            return of([]);
          }

          return forkJoin(
            mediaAssets.map(({ asset, media }) =>
              media.id
                ? this.assetService.resolveBrowserUrl(asset).pipe(
                    map((resolved) => ({
                      ...media,
                      url: resolved.url || media.url,
                      fileUrl: resolved.fileUrl || media.fileUrl,
                    })),
                    catchError(() => of(media)),
                  )
                : of(media),
            ),
          );
        }),
      )
      .pipe(finalize(() => this.isLoadingLibrary.set(false)))
      .subscribe({
        next: (mediaAssets) => {
          this.revokeLibraryObjectUrls();
          this.libraryMediaAssets.set(mediaAssets);
        },
        error: (error: Error) => {
          this.errorMessage.set(
            error?.message ?? this.tr('textEditor.mediaLibraryLoadError'),
          );
        },
      });
  }

  private toEditorMedia(asset: WorkspaceAssetModel): EditorMedia | null {
    const id = this.toNumber(asset.id, 0);
    const url = this.toString(asset.storagePath ?? asset.sourceUrl).trim();
    if (!id || !url || !this.isSupportedLibraryAssetType(asset.assetType)) {
      return null;
    }

    return {
      source: 'library',
      workspaceAssetId: id,
      id,
      assetType: asset.assetType,
      url,
      fileUrl: url,
      fileName:
        this.toString(asset.originalFileName ?? asset.title).trim() ||
        this.tr('textEditor.mediaUntitled'),
      mimeType: this.toString(asset.mimeType).trim(),
      altText: '',
      fileSizeBytes: this.toNullableNumber(asset.fileSizeBytes),
    };
  }

  private isAcceptedImageUpload(file: File): boolean {
    if (
      file.size <= 0 ||
      file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES ||
      file.name.length > MAX_IMAGE_FILE_NAME_LENGTH
    ) {
      return false;
    }

    const extension = file.name.split('.').pop()?.trim().toLowerCase() ?? '';
    const expectedMimeType = IMAGE_MIME_TYPES_BY_EXTENSION[extension];
    return Boolean(
      expectedMimeType && file.type.trim().toLowerCase() === expectedMimeType,
    );
  }

  private isSupportedLibraryAssetType(
    assetType: WorkspaceAssetType,
  ): assetType is
    | WorkspaceAssetType.Image
    | WorkspaceAssetType.Audio
    | WorkspaceAssetType.Video {
    return (
      assetType === WorkspaceAssetType.Image ||
      assetType === WorkspaceAssetType.Audio ||
      assetType === WorkspaceAssetType.Video
    );
  }

  private resolveMediaPreviewType(media: EditorMedia): MediaPreviewType {
    if (media.assetType === WorkspaceAssetType.Video) {
      return 'video';
    }

    if (media.assetType === WorkspaceAssetType.Audio) {
      return 'audio';
    }

    if (media.assetType === WorkspaceAssetType.Image) {
      return 'image';
    }

    const mimeType = (media.mimeType ?? '').trim().toLowerCase();
    if (mimeType.startsWith('video/')) {
      return 'video';
    }

    if (mimeType.startsWith('audio/')) {
      return 'audio';
    }

    return 'image';
  }

  private toNumber(value: number | string | null | undefined, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private toNullableNumber(value: number | string | null | undefined) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toString(value: string | null | undefined): string {
    return value ?? '';
  }

  private revokeLibraryObjectUrls(): void {
    for (const media of this.libraryMediaAssets()) {
      if (
        media.url.startsWith('blob:') &&
        media.url !== this.selectedObjectUrlToKeep
      ) {
        globalThis.URL?.revokeObjectURL(media.url);
      }
    }
  }

  private showGeneratedImagesLimit(): void {
    this.toastr.error(this.tr('common.generatedImagesLimitReached'));
  }
}
